import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import { MedisanteTargetSystem } from '../../../../pages/TargetSystems/types';
import FormHelperText from '@material-ui/core/FormHelperText';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import {
  hasOneOfOrgRoles,
  Role,
} from '../../../../contexts/AuthProvider/types';
import { orgValidate } from '../../hooks';
import { useManageContext } from '../../../../contexts/ManageContext/ManageContext';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import MedSelect from '../../../../eliot-components/src/Common/MedSelect';
import { useHistory } from 'react-router-dom';
import { Alert } from '@material-ui/lab';
import { Link } from '@material-ui/core';
import UnlinkAll from '../DeviceLinking/UnlinkAll';
import {
  getGatewaySensors,
  getSelectedSensors,
} from '../DeviceLinking/provider';
import { SelectedDevicesStatus, Props } from './types';
import useStyles from './styles';
import { useGateways } from './hooks';
import { selectedDevicesStatusReducer } from './provider';
import {
  allBluetoothDevices,
  allGatewayDevices,
} from '../../../../pages/Device/constants';
import { DeviceModels } from '../../../../pages/Device/types';
import {
  useGetTargetSystemsQuery,
  useTransferOwnerMutation,
} from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';
import { notFullySuportedModel } from '../../../../pages/TargetSystems/config';
import { Warning } from '@material-ui/icons';
import { UnsupportedHL7Types } from '../../../../pages/TargetSystems/components/EditTargetSystemDialog/provider';

const TransferOwnershipDialog: FunctionComponent<Props> = memo(
  ({ devices, isOpen = false, onClose, onCreated }) => {
    const classes = useStyles();
    const config = useConfig();
    const { authUser } = useAuthUser();
    const { state, dispatch } = useManageContext();
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const [targetSystemIDs, setTargetSystemIDs] = useState<string[]>([]);
    const selectedOrgs =
      authUser.selectedOrgs.length === 1
        ? authUser.selectedOrgs
        : authUser.orgs.map((o) => o.id);
    const [transferOwnership, { isLoading }] = useTransferOwnerMutation();

    const {
      gateways,
      error: gwError,
      isLoading: gwLoading,
    } = useGateways(devices);

    const [isUnlinkAllOpened, setIsUnlinkAllOpened] = useState(false);
    const [owner, setOwner] = useState('');
    const { data: availableTargetSystems = [] } = useGetTargetSystemsQuery({
      apiBaseURL: config.apiBaseURL,
      orgs: owner ? [owner] : [],
      filters: [],
    });

    const [selectedDevicesStatus, updateSelectedDevicesStatus] = useReducer(
      selectedDevicesStatusReducer,
      SelectedDevicesStatus.INITIAL
    );

    useEffect(() => {
      updateSelectedDevicesStatus({
        devices,
        gateways,
        reset: true,
      });
    }, [devices, gateways]);

    useEffect(() => {
      if (availableTargetSystems && owner) {
        const selected = availableTargetSystems.filter((ts) => ts.isDefault);
        if (selected) {
          setTargetSystemIDs(selected.map((ts) => ts.id));
        }
      }
    }, [availableTargetSystems, owner]);

    const handleClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
    }, [onClose]);

    const handleCreated = useCallback(() => {
      if (onCreated) {
        onCreated();
      }
    }, [onCreated]);

    const targetSystemFromID = (id: string): MedisanteTargetSystem => {
      return (
        availableTargetSystems.find((ts) => ts.id === id) || {
          id,
          name: '',
          url: '',
          isDefault: false,
        }
      );
    };

    const availableOwners = authUser.orgs.filter(
      (o) =>
        hasOneOfOrgRoles(authUser, [o.id], [Role.DeviceManager]) &&
        selectedOrgs &&
        (selectedOrgs.length === 1 || devices.length === 1) &&
        devices[0].owner !== o.id &&
        !o.demo
    );

    const handleSubmit = async () => {
      const err = orgValidate(selectedOrgs, devices);
      if (!err) {
        const deviceIDs = devices.map((d) => d.id);
        try {
          const response = await transferOwnership({
            orgID: devices[0].owner,
            owner,
            targetSystemIDs,
            apiBaseURL: config.apiBaseURL,
            deviceIDs,
          });
          if (response.hasOwnProperty('data')) {
            handleClose();
            handleCreated();
            dispatch({
              type: 'transfer-ownership',
              data: true,
            });
            history.push('/devices');
          }
          return;
        } catch (e: any) {
          enqueueSnackbar(parseErrorMessage(e), { variant: 'error' });
          return;
        }
      }
      enqueueSnackbar(err, { variant: 'warning' });
    };

    const onUnlinkAllClose = (unlinkAll: boolean = false) => {
      updateSelectedDevicesStatus({
        devices,
        gateways,
        isUnlinked: unlinkAll,
      });

      setIsUnlinkAllOpened(false);
    };

    const getSensorsToUnlink = () => {
      const selectedGateways = gateways.filter((g) =>
        devices.some((d) => d.id === g.id)
      );
      return selectedDevicesStatus ===
        SelectedDevicesStatus.LINKED_SENSORS_SELECTED
        ? getSelectedSensors(devices)
        : SelectedDevicesStatus.GATEWAY_SELECTED
        ? getGatewaySensors(selectedGateways)
        : [];
    };

    const isFromDemoOrganisation = () => {
      const devicesOrgsIds = devices.map((d) => d.owner);
      const devicesOrgs = state.userOrgs?.filter((o) =>
        devicesOrgsIds.includes(o.id)
      );
      return !!devicesOrgs?.some((o) => o.demo);
    };

    const isFromUnlinkedOrg = () => {
      return devices.some((d) => d.allowUnlinkedGateway);
    };

    const isSensors =
      devices.filter((d) =>
        allBluetoothDevices.includes(d.model as DeviceModels)
      ).length > 0;

    const isGateway =
      devices.filter((d) => allGatewayDevices.includes(d.model as DeviceModels))
        .length > 0;

    const targetSystemIncompatible = availableTargetSystems
      .filter((ts) => {
        const notSupportedDevices = devices.filter(
          (d) => notFullySuportedModel.indexOf(d.model) !== -1
        );

        return (
          notSupportedDevices.length > 0 &&
          ts.format &&
          ts.format.match(/fhir/i)
        );
      })
      .map((ts) => ts.id);

    const canTransferWebhooks = devices.some((d) => !!d.allowUnlinkedGateway);

    return (
      <>
        <Dialog
          aria-labelledby="dialog-title"
          open={isOpen}
          onClose={() => {
            handleClose();
          }}
        >
          <DialogTitle id="dialog-title">Move</DialogTitle>
          <DialogContent>
            <MedSelect
              disabled={
                selectedDevicesStatus ===
                  SelectedDevicesStatus.LINKED_SENSORS_SELECTED &&
                !isFromUnlinkedOrg()
              }
              name="owner"
              placeholder="Choose Organisation"
              value={owner}
              options={availableOwners}
              valueGetter={(o) => o.id}
              optionTextGetter={(o) => o.name}
              onChange={(e) => {
                setOwner(e.target.value as string);
                setTargetSystemIDs([]);
              }}
            />
            {(isSensors && !canTransferWebhooks) ||
            (isGateway && canTransferWebhooks) ? null : (
              <MedSelect
                disabled={
                  (selectedDevicesStatus ===
                    SelectedDevicesStatus.LINKED_SENSORS_SELECTED ||
                    !owner) &&
                  !isFromUnlinkedOrg()
                }
                multiple
                name="target-system"
                placeholder="Webhooks"
                value={targetSystemIDs}
                onChange={(e: any) => setTargetSystemIDs(e.target.value)}
                options={availableTargetSystems}
                valueGetter={(ts) => ts.id}
                optionTextGetter={(ts) => {
                  if (targetSystemIncompatible.indexOf(ts.id) !== -1) {
                    return (
                      <>
                        <Tooltip
                          title={`Devices and/or measurements may not be supported wholly or partly`}
                        >
                          <Warning htmlColor={'rgb(250, 84, 54)'} />
                        </Tooltip>
                        {ts.name}
                      </>
                    );
                  } else {
                    return ts.name;
                  }
                }}
                emptyMessage="No available webhooks!"
                renderValue={(selectedTargetSystems) => {
                  return (
                    <div className={classes.chips}>
                      {(selectedTargetSystems as string[])
                        .map(targetSystemFromID)
                        .map((ts: MedisanteTargetSystem) => (
                          <Tooltip
                            key={ts.id}
                            className={classes.tooltip}
                            title={ts.url}
                          >
                            <Chip
                              label={ts.name}
                              className={classes.chip}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              onDelete={() => {
                                setTargetSystemIDs(
                                  targetSystemIDs.filter((id) => id !== ts.id)
                                );
                              }}
                            />
                          </Tooltip>
                        ))}
                    </div>
                  );
                }}
              />
            )}
            {[
              SelectedDevicesStatus.INITIAL,
              SelectedDevicesStatus.ALL_SET,
            ].includes(selectedDevicesStatus) ? (
              <FormHelperText>
                Webhook required for measurement routing
              </FormHelperText>
            ) : null}

            {selectedDevicesStatus ===
              SelectedDevicesStatus.LINKED_SENSORS_SELECTED &&
            !isFromUnlinkedOrg() ? (
              <Alert className={classes.alert} severity="error">
                All the devices must be unlinked to change the ownership!{' '}
                <Link onClick={() => setIsUnlinkAllOpened(true)}>
                  Click here
                </Link>{' '}
                to unlink all devices!
              </Alert>
            ) : null}

            {selectedDevicesStatus === SelectedDevicesStatus.GATEWAY_SELECTED &&
            !isFromUnlinkedOrg() ? (
              <Alert className={classes.alert} severity="warning">
                Gateway is selected. If you don't want to transfer all the
                sensors linked to this gateway{' '}
                <Link onClick={() => setIsUnlinkAllOpened(true)}>
                  click here
                </Link>{' '}
                and check if there is some linked sensors .
              </Alert>
            ) : null}

            {targetSystemIncompatible.find(
              (id) => targetSystemIDs.indexOf(id) !== -1
            ) && (
              <Alert severity="warning" style={{ marginTop: '1em' }}>
                Not all devices and/or measurement types are supported,
                including (but not limited to):
                <div style={{ marginTop: '1em' }}>
                  {UnsupportedHL7Types.length ? (
                    <>
                      <strong>Measurement Types:</strong>
                      <ul>
                        {UnsupportedHL7Types.map((type) => (
                          <li key={type}>{type}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </Button>
            <ButtonProgress
              variant="contained"
              color="primary"
              isLoading={isLoading}
              disabled={isFromDemoOrganisation()}
              onClick={handleSubmit}
            >
              Submit
            </ButtonProgress>
          </DialogActions>

          <UnlinkAll
            open={isUnlinkAllOpened}
            onClose={onUnlinkAllClose}
            sensors={getSensorsToUnlink()}
            gateways={gateways}
            errorMessage={
              gwError && 'message' in gwError ? gwError.message : undefined
            }
            isLoading={gwLoading}
          />
        </Dialog>
      </>
    );
  }
);

export default TransferOwnershipDialog;
